import { useCallback, useState } from 'react';

import { ApprovalDecision, UpdateInboxEntryInput } from '@this+that/models';
import log from 'loglevel';

import { useActiveInboxEntriesSlice } from './useActiveInboxEntriesSlice';
import { useUpdateInboxEntry } from './useUpdateInboxEntry';
import { useUnreadStore } from '../../store';
import { pushGTMEvent } from '../../utils/analytics/gtm';

export const useMarkInboxEntryAsResolved = () => {
  const { handleUpdateInboxEntry } = useUpdateInboxEntry();
  const { saveDetailedInboxEntry } = useActiveInboxEntriesSlice();
  const { markAsRead } = useUnreadStore();
  const [isResolving, setIsResolving] = useState(false);

  const markAsResolved = useCallback(
    async (
      inboxEntryId: string,
      approvalDecision?: ApprovalDecision,
    ): Promise<boolean> => {
      setIsResolving(true);
      try {
        const resolvedAt = Date.now();
        const updateData: UpdateInboxEntryInput = {
          id: inboxEntryId,
          resolvedAt,
          isSuggestion: false,
          ...(approvalDecision && { approvalDecision }),
        };

        const updatedEntry = await handleUpdateInboxEntry(updateData);

        if (updatedEntry) {
          pushGTMEvent({ event: 'dobox_resolved' });
          saveDetailedInboxEntry(updatedEntry);
          markAsRead(updatedEntry.id, updatedEntry.taskGroupId);
          log.info(`Inbox entry ${inboxEntryId} marked as resolved`);
          return true;
        }

        return false;
      } catch (error) {
        log.error('Error marking inbox entry as resolved:', error);
        return false;
      } finally {
        setIsResolving(false);
      }
    },
    [handleUpdateInboxEntry, saveDetailedInboxEntry, markAsRead],
  );

  return {
    markAsResolved,
    isResolving,
  };
};
